import { useMemo, useCallback, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import DriveInputNode from './nodes/DriveInputNode';
import ClaudeVisionNode from './nodes/ClaudeVisionNode';
import OutputDesignerNode from './nodes/OutputDesignerNode';
import SheetOutputNode from './nodes/SheetOutputNode';
import DriveMoveNode from './nodes/DriveMoveNode';
import DriveInputConfig from '../config-panels/DriveInputConfig';
import ClaudeVisionConfig from '../config-panels/ClaudeVisionConfig';
import OutputDesignerConfig from '../config-panels/OutputDesignerConfig';
import SheetOutputConfig from '../config-panels/SheetOutputConfig';
import DriveMoveConfig from '../config-panels/DriveMoveConfig';
import type { IWorkflowNode, IWorkflowEdge, INodeConfig, NodeType } from '@/types/docflow';
import { NODE_TYPE_LABELS } from '@/types/docflow';
import { useTranslation } from 'react-i18next';

const nodeTypes = {
  drive_input: DriveInputNode,
  claude_vision: ClaudeVisionNode,
  output_designer: OutputDesignerNode,
  sheet_output: SheetOutputNode,
  drive_move: DriveMoveNode,
} as const;

const NODE_WIDTH = 200;
const NODE_HEIGHT = 120;

function getLayoutedElements(nodes: Node[], edges: Edge[], direction: 'LR' | 'TB' = 'LR') {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, nodesep: 60, ranksep: 120 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const pos = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

// Convert workflow nodes/edges to React Flow format
function toFlowNodes(workflowNodes: IWorkflowNode[], selectedId?: string): Node[] {
  return workflowNodes.map((wn) => ({
    id: wn.id,
    type: wn.type,
    position: wn.position,
    data: {
      label: wn.label,
      config: wn.config,
      selected: wn.id === selectedId,
    },
    draggable: true,
  }));
}

function toFlowEdges(workflowEdges: IWorkflowEdge[]): Edge[] {
  return workflowEdges.map((we) => ({
    id: we.id,
    source: we.source,
    target: we.target,
    markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
    style: { strokeWidth: 2 },
  }));
}

// Ordered node sequence for validation
const NODE_ORDER: NodeType[] = ['drive_input', 'claude_vision', 'output_designer', 'sheet_output', 'drive_move'];

interface DocFlowCanvasProps {
  workflowNodes: IWorkflowNode[];
  workflowEdges: IWorkflowEdge[];
  onNodesChange: (nodes: IWorkflowNode[]) => void;
  onEdgesChange: (edges: IWorkflowEdge[]) => void;
}

const DocFlowCanvas: React.FC<DocFlowCanvasProps> = ({
  workflowNodes,
  workflowEdges,
  onNodesChange,
  onEdgesChange,
}) => {
  const { t } = useTranslation('docflow');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Build React Flow nodes/edges
  const initialFlowNodes = useMemo(() => {
    const flowNodes = toFlowNodes(workflowNodes, selectedNodeId || undefined);
    const flowEdges = toFlowEdges(workflowEdges);
    // Only auto-layout if all positions are at origin
    const needsLayout = workflowNodes.length > 0 && workflowNodes.every((n) => n.position.x === 0 && n.position.y === 0);
    if (needsLayout) {
      return getLayoutedElements(flowNodes, flowEdges, 'LR');
    }
    return { nodes: flowNodes, edges: flowEdges };
  }, [workflowNodes, workflowEdges, selectedNodeId]);

  const [nodes, setNodes, onRFNodesChange] = useNodesState(initialFlowNodes.nodes);
  const [edges, setEdges, onRFEdgesChange] = useEdgesState(initialFlowNodes.edges);

  // Sync when workflow data changes externally
  useMemo(() => {
    setNodes(initialFlowNodes.nodes);
    setEdges(initialFlowNodes.edges);
  }, [initialFlowNodes, setNodes, setEdges]);

  const onConnect = useCallback(
    (connection: Connection) => {
      // Validate connection order
      const sourceNode = workflowNodes.find((n) => n.id === connection.source);
      const targetNode = workflowNodes.find((n) => n.id === connection.target);
      if (!sourceNode || !targetNode) return;

      const sourceIdx = NODE_ORDER.indexOf(sourceNode.type);
      const targetIdx = NODE_ORDER.indexOf(targetNode.type);
      if (sourceIdx >= targetIdx) return; // Only allow forward connections

      const newEdge: IWorkflowEdge = {
        id: `e-${connection.source}-${connection.target}`,
        source: connection.source!,
        target: connection.target!,
      };

      setEdges((eds) => addEdge({
        ...connection,
        id: newEdge.id,
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2 },
      }, eds));

      onEdgesChange([...workflowEdges, newEdge]);
    },
    [workflowNodes, workflowEdges, onEdgesChange, setEdges]
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  // Add a new node
  const addNode = useCallback((type: NodeType) => {
    const id = `${type}-${Date.now()}`;
    const newNode: IWorkflowNode = {
      id,
      type,
      label: NODE_TYPE_LABELS[type],
      position: { x: workflowNodes.length * 280, y: 100 },
      config: {},
    };
    onNodesChange([...workflowNodes, newNode]);
    setSelectedNodeId(id);
  }, [workflowNodes, onNodesChange]);

  // Update selected node's config
  const updateNodeConfig = useCallback((config: INodeConfig) => {
    if (!selectedNodeId) return;
    const updated = workflowNodes.map((n) =>
      n.id === selectedNodeId ? { ...n, config } : n
    );
    onNodesChange(updated);
  }, [selectedNodeId, workflowNodes, onNodesChange]);

  // Delete selected node
  const deleteSelectedNode = useCallback(() => {
    if (!selectedNodeId) return;
    onNodesChange(workflowNodes.filter((n) => n.id !== selectedNodeId));
    onEdgesChange(workflowEdges.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
  }, [selectedNodeId, workflowNodes, workflowEdges, onNodesChange, onEdgesChange]);

  const selectedNode = workflowNodes.find((n) => n.id === selectedNodeId);

  return (
    <div className="flex h-[600px]">
      {/* Canvas */}
      <div className="flex-1 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 relative">
        {/* Add Node Toolbar */}
        <div className="absolute top-3 left-3 z-10 flex gap-1">
          {NODE_ORDER.map((type) => (
            <button
              key={type}
              onClick={() => addNode(type)}
              className="text-xs px-2 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
              title={NODE_TYPE_LABELS[type]}
            >
              + {NODE_TYPE_LABELS[type]}
            </button>
          ))}
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onRFNodesChange}
          onEdgesChange={onRFEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes as any}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.3}
          maxZoom={1.5}
        >
          <Background color="#94a3b8" gap={20} size={1} />
          <Controls className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" />
        </ReactFlow>
      </div>

      {/* Config Panel */}
      {selectedNode && (
        <div className="w-96 ml-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              {t('node.configure')}
            </h3>
            <button
              onClick={deleteSelectedNode}
              className="text-xs px-2 py-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
            >
              {t('workflow.delete')}
            </button>
          </div>

          {/* Node label */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Label
            </label>
            <input
              type="text"
              value={selectedNode.label}
              onChange={(e) => {
                const updated = workflowNodes.map((n) =>
                  n.id === selectedNodeId ? { ...n, label: e.target.value } : n
                );
                onNodesChange(updated);
              }}
              className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-gray-200"
            />
          </div>

          {/* Type-specific config */}
          {selectedNode.type === 'drive_input' && (
            <DriveInputConfig config={selectedNode.config} onChange={updateNodeConfig} />
          )}
          {selectedNode.type === 'claude_vision' && (
            <ClaudeVisionConfig config={selectedNode.config} onChange={updateNodeConfig} />
          )}
          {selectedNode.type === 'output_designer' && (
            <OutputDesignerConfig config={selectedNode.config} onChange={updateNodeConfig} />
          )}
          {selectedNode.type === 'sheet_output' && (
            <SheetOutputConfig config={selectedNode.config} onChange={updateNodeConfig} />
          )}
          {selectedNode.type === 'drive_move' && (
            <DriveMoveConfig config={selectedNode.config} onChange={updateNodeConfig} />
          )}
        </div>
      )}
    </div>
  );
};

export default DocFlowCanvas;
